import _ from 'lodash';
import type {
  BasicValueOption,
  FieldId,
  FieldValue,
  FormFieldDef,
  FormSchema,
} from '../types';

export enum ElementType {
  Section = 'Section',
  Field = 'Field',
}

export enum FieldType {
  Radio = 'RADIO',
  CheckboxGrouping = 'CHECKBOX_GROUPING',
  Checkbox = 'CHECKBOX',
  Text = 'TEXT',
  Currency = 'CURRENCY',
  Date = 'DATE',
  ItemizedPercentileList = 'ITEMIZED_PERCENTILE_LIST',
  GroupedPercentileList = 'GROUPED_PERCENTILE_LIST',
  JobList = 'JOB_LIST',
  FieldList = 'FIELD_LIST',
  NaicsClassDescription = 'NAICS_CLASS_DESCRIPTION',
  // * RE Form Controls
  SingleSelect = 'SINGLE_SELECT',
  MultiSelect = 'MULTI_SELECT',
  Dropdown = 'DROPDOWN',
  Number = 'NUMBER',
}

export const getDefaultFormValues = (
  formSchema: FormSchema,
  uiDefaultValues: Record<string, any> | undefined,
  useDefaultValuesFromFormSchema: boolean
) => {
  return Object.values(formSchema.fields).reduce(
    (acc: Record<FieldId, FieldValue>, fieldDef: FormFieldDef) => {
      const savedValue = formSchema.values[fieldDef.id];
      const { defaultValue } = fieldDef;
      const uiDefaultValue = uiDefaultValues?.[fieldDef.id];

      const fallbackValue = getFallbackValueForFieldType(fieldDef.type);
      const _value =
        savedValue ??
        uiDefaultValue ??
        (useDefaultValuesFromFormSchema ? defaultValue : null) ??
        fallbackValue;

      return { ...acc, [fieldDef.id]: _value };
    },
    {}
  );
};

const getFallbackValueForFieldType = (fieldType: FieldType) => {
  if (fieldType == FieldType.ItemizedPercentileList) {
    return {
      props: {},
    };
  }

  if (fieldType == FieldType.GroupedPercentileList) {
    return {
      values: [],
    };
  }

  if (fieldType == FieldType.NaicsClassDescription) {
    return {
      props: null,
    };
  }

  if (fieldType == FieldType.JobList || fieldType == FieldType.FieldList) {
    return {
      values: [],
    };
  }

  if (
    fieldType == FieldType.CheckboxGrouping ||
    fieldType == FieldType.MultiSelect
  ) {
    return {
      comment: '',
      values: [],
    };
  }

  if (fieldType == FieldType.Checkbox || fieldType == FieldType.Radio) {
    return {
      value: '',
      comment: '',
    };
  }

  if (fieldType == FieldType.Number) {
    return {
      value: null,
    };
  }

  return {
    value: '',
  };
};

/** Whether a field's value counts as an answer: what a save sends, and what a host counts as answered. */
export const isAnswered = (fieldValue: unknown): boolean =>
  !_.isEmpty(fieldValue) && determineIsAnswered(fieldValue);

export const pickAnsweredFields = (formData: Record<string, FieldValue>) => {
  return Object.keys(formData).reduce((answeredFields, fieldKey) => {
    const fieldVal = formData[fieldKey];
    if (!isAnswered(fieldVal)) {
      return answeredFields;
    }

    return { ...answeredFields, [fieldKey]: fieldVal };
  }, {});
};

function determineIsAnswered(fieldValue: any | null): boolean {
  if (fieldValue == null) {
    return false;
  }

  if (Array.isArray(fieldValue.values)) {
    return true;
  }

  if (
    typeof fieldValue.value == 'object' ||
    typeof fieldValue.values == 'object' ||
    typeof fieldValue.props == 'object'
  ) {
    const nestedVals = {
      ...fieldValue.value,
      ...fieldValue.values,
      ...fieldValue.props,
    };

    return Object.values(nestedVals).reduce(
      (isAnswered: boolean, nestedValue: any) =>
        isAnswered || determineIsAnswered(nestedValue),
      false
    ) as unknown as boolean;
  }

  return (
    typeof fieldValue.value == 'number' ||
    (fieldValue.value?.length ?? 0) > 0 ||
    (fieldValue.values?.length ?? 0) > 0 ||
    (fieldValue.comment?.length ?? 0) > 0
  );
}

export const getRadioOptions = (valueOptions: BasicValueOption[] = []) => {
  return getControlOptions(valueOptions).map((opt) => {
    if (String(opt.value).toLowerCase() == 'other') {
      return { ...opt, commentEligibility: 'REQUIRED' };
    }

    return opt;
  });
};

export const getControlOptions = (valueOptions: BasicValueOption[] = []) => {
  return valueOptions.map((opt) => ({
    ...opt,
    label: opt.text,
    value: opt.value,
  }));
};

export const getIsOptionEligibleForComment = (
  option: Pick<BasicValueOption, 'commentEligibility'>
) => {
  return option.commentEligibility === 'REQUIRED';
};

export const getListItems = (valueOptions: BasicValueOption[] = []) => {
  return valueOptions.map((opt) => ({
    ...opt,
    label: opt.text,
    key: String(opt.value),
  }));
};

export function isObjectShapeOf(
  expected: Record<string, unknown>,
  actual: Record<string, unknown>
) {
  return (
    JSON.stringify(Object.keys(expected)) == JSON.stringify(Object.keys(actual))
  );
}
